import React, { useContext, useEffect, useState } from 'react';
import Row from '@amzn/meridian/row';
import Heading from '@amzn/meridian/heading';
import Text from '@amzn/meridian/text';
import Box from '@amzn/meridian/box';
import { ReportingTeamUser } from '../../../Types';
import CustomziedSideMenu from './Components/SideMenu/CustomizedSideMenu';
import RichCardCollection from './Components/Card/RichCardCollection';
import Divider from '@amzn/meridian/divider';
import { FormattedMessage, useIntl } from 'react-intl';
import Select, { SelectOption } from '@amzn/meridian/select';
import { cardGrid } from './Util/AssociateRichCardsStyles';
import {
  ARCFilters,
  AssociateCardProps,
  isValidARCFilter,
  RELATION_TO_USER,
  SIDE_MENU_PATH,
} from './Types/types';
import AssociateCardExpanded from './AssociateCardExpanded';
import { StateContext } from '../../../GlobalStateStore';
import DataState from '../../../Types/DataState';
import Loader from '@amzn/meridian/loader';
import Alert from '@amzn/meridian/alert';
import { Feature, isFeatureEnabled } from '../../../config/FeaturesConfig';
import Column from '@amzn/meridian/column';
import { EPOCH_START } from '../../../Util/GlobalConstants';
import FilterDescription from './Util/FilterDescription';
import ARCHeading from './Util/ARCHeading';
import moment from 'moment';
import { useSearchParams } from 'react-router-dom';

const AssociateCardsContainer: React.FC<AssociateCardProps> = ({
  defaultVertical,
  teamProfile,
  associateCardSummary,
  summaryState,
  metricState,
  insightState,
  goalState,
  verticalOptions,
  verticalValue,
  setVerticalValue,
}: AssociateCardProps): React.ReactElement => {
  const globalState = useContext(StateContext);
  const { inspireOptions } = globalState.state;
  const { isTm, isOm, requesterUserID, requesterUsername } =
    inspireOptions.data;
  const [hash, setHash] = useState(
    isOm ? SIDE_MENU_PATH.ALL_TMS : SIDE_MENU_PATH.ALL_ASSOCIATES
  );
  const [openLinkHash, setOpenLinkHash] = useState(
    isOm ? SIDE_MENU_PATH.ALL_TMS : SIDE_MENU_PATH.ALL_ASSOCIATES
  );
  const [expanded, setExpanded] = useState(false);
  const [focusProfile, setFocusProfile] = useState<ReportingTeamUser | null>(
    null
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [searchParams] = useSearchParams();
  const filterSearchParam = searchParams.get('filter');
  const defaultFilter = isValidARCFilter(filterSearchParam)
    ? filterSearchParam
    : ARCFilters.PERFORMANCE;
  const [filter, setFilter] = useState<string>(defaultFilter);
  const intl = useIntl();
  const [lastestMetricPublishDate, setLastMetricPublishDate] =
    useState(EPOCH_START);

  const commonSelectClickstreamData = {
    'data-csa-c-type': 'select',
    'data-csa-c-slot-id': 'arc-mainview-filter-by-slot',
    'data-csa-c-tag1': 'Team',
    'data-csa-c-tag2': 'Associates',
    'data-csa-c-profile-id': requesterUserID,
    'data-csa-c-username': requesterUsername,
    'data-csa-c-is-tm': isTm,
  };

  const allTmCount = teamProfile.reportingTeam.filter(
    profile => profile.isManager
  ).length;
  const allAssociatesCount = teamProfile.reportingTeam.filter(
    profile => !profile.isManager
  ).length;
  const directAssociatesCount = teamProfile.reportingTeam.filter(
    profile =>
      profile.relatedUser.relationToUser === RELATION_TO_USER.DIRECT_REPORT
  ).length;
  const sharedProfileCount = teamProfile.reportingTeam.filter(
    profile => profile.relatedUser.relationToUser === RELATION_TO_USER.COACHEE
  ).length;
  const enableGoalSetting = isFeatureEnabled(Feature.GOAL_SETTING);

  useEffect(() => {
    if (
      metricState === DataState.LOADED &&
      associateCardSummary[0] &&
      associateCardSummary[0].performance
    ) {
      if (associateCardSummary.length > 0) {
        const latestDate = associateCardSummary.sort(
          (summaryA, summaryB) =>
            new Date(summaryB.performance?.metricTimestamp!).getTime() -
            new Date(summaryA.performance?.metricTimestamp!).getTime()
        )[0].performance?.metricTimestamp;

        setLastMetricPublishDate(latestDate ?? EPOCH_START);
      }
    }
  }, [metricState, associateCardSummary]);

  if (
    metricState === DataState.LOADING ||
    insightState === DataState.LOADING ||
    goalState === DataState.LOADING
  ) {
    return (
      <Row alignmentHorizontal='center'>
        <Loader />
      </Row>
    );
  }

  if (
    summaryState === DataState.ERROR ||
    metricState === DataState.ERROR ||
    insightState === DataState.ERROR ||
    goalState === DataState.ERROR
  ) {
    return (
      <Alert type='error'>
        <FormattedMessage
          id='cs_perfportal_error_refresh_page'
          defaultMessage='Something went wrong. Please try refreshing the page. If the problem persists, please submit a SIM ticket for us to troubleshoot.'
        />
      </Alert>
    );
  }

  return (
    <>
      {!isOm && (
        <>
          <Row alignmentHorizontal='justify' width='100%' widths='fill'>
            <Row
              spacingInset='none'
              widths='fit'
              width='100%'
              alignmentVertical='top'
              alignmentHorizontal='justify'
            >
              <Column spacing='none'>
                <Heading level={2} type='h500'>
                  <FormattedMessage
                    id='cs_perfportal_associates'
                    defaultMessage='Associates'
                  />
                </Heading>
                {!expanded && filter === ARCFilters.PERFORMANCE && (
                  <Text type='b400' color='secondary'>
                    <FormattedMessage
                      id='cs_perfportal_quartile_desc'
                      defaultMessage="Quartile status is based on the previous month's data and shown for Direct Report associates only."
                    />
                  </Text>
                )}
              </Column>
              {!expanded && (
                <Column spacing='none'>
                  <Row alignmentHorizontal='end' alignmentVertical='top'>
                    <Select
                      label={intl.formatMessage({
                        id: 'cs_perfportal_filterBy',
                        defaultMessage: 'Filter by',
                      })}
                      value={filter}
                      onChange={setFilter}
                      width={300}
                      disabled={expanded}
                      data-cy='filter-by'
                      size='xlarge'
                    >
                      <SelectOption
                        value={ARCFilters.COACHINGS}
                        label={intl.formatMessage({
                          id: 'cs_perfportal_coachings',
                          defaultMessage: 'Coachings',
                        })}
                        {...commonSelectClickstreamData}
                      />
                      {enableGoalSetting && (
                        <SelectOption
                          value={ARCFilters.GOALS}
                          label={intl.formatMessage({
                            id: 'cs_perfportal_arc_active_goals_heading',
                            defaultMessage: 'Active Goals',
                          })}
                          {...commonSelectClickstreamData}
                        />
                      )}
                      <SelectOption
                        value={ARCFilters.OPPORTUNITIES}
                        label={intl.formatMessage({
                          id: 'cs_perfportal_opportunities',
                          defaultMessage: 'Opportunities',
                        })}
                        {...commonSelectClickstreamData}
                      />
                      <SelectOption
                        value={ARCFilters.PERFORMANCE}
                        label={intl.formatMessage({
                          id: 'cs_perfportal_performance',
                          defaultMessage: 'Performance',
                        })}
                        {...commonSelectClickstreamData}
                      />
                      <SelectOption
                        value={ARCFilters.KUDOS}
                        label={intl.formatMessage({
                          id: 'cs_perfportal_kudos',
                          defaultMessage: 'Kudos',
                        })}
                        {...commonSelectClickstreamData}
                      />
                    </Select>
                  </Row>
                  {lastestMetricPublishDate !== EPOCH_START && (
                    <Row spacingInset='100 none none'>
                      <Text type='b200' color='secondary'>
                        <FormattedMessage
                          id='cs_perfportal_lastUpdated'
                          defaultMessage='Last updated'
                        />{' '}
                        {moment(lastestMetricPublishDate)
                          .utc()
                          .format('YYYY-MM-DD HH:mm')}{' '}
                        UTC
                      </Text>
                    </Row>
                  )}
                </Column>
              )}
            </Row>
          </Row>
          <Row width='100%' spacingInset='none' spacing='none'>
            <Divider />
          </Row>
        </>
      )}
      <Row alignmentVertical='stretch' spacing='400' data-cy='side-panel'>
        <CustomziedSideMenu
          teamProfile={teamProfile}
          hash={hash}
          setHash={setHash}
          openLinkHash={openLinkHash}
          setOpenLinkHash={setOpenLinkHash}
          expanded={expanded}
          setExpanded={setExpanded}
          setFocusProfile={setFocusProfile}
        />

        <Box width={'100%'}>
          <Row spacingInset='none none 200 none' widths='fill'>
            <ARCHeading
              hash={hash}
              allTmCount={allTmCount}
              allAssociateCount={allAssociatesCount}
              directReportCount={directAssociatesCount}
              sharedProfileCount={sharedProfileCount}
              filter={filter}
              verticalOptions={verticalOptions}
              verticalValue={verticalValue}
              setVerticalValue={setVerticalValue}
            />
          </Row>
          {!expanded && (
            <Row spacingInset='none none 200 none'>
              <FilterDescription
                filter={filter}
                vertical={defaultVertical}
                hash={hash}
                allTmCount={allTmCount}
                allAssociateCount={allAssociatesCount}
                directReportCount={directAssociatesCount}
                sharedProfileCount={sharedProfileCount}
              />
            </Row>
          )}
          <div style={cardGrid}>
            {!expanded && (
              <RichCardCollection
                hash={hash}
                teamProfile={teamProfile}
                associateCardSummary={associateCardSummary}
                filter={filter}
                openLinkHash={openLinkHash}
                currentPage={currentPage}
                currentVertical={verticalValue ?? defaultVertical}
                setCurrentPage={setCurrentPage}
                setHash={setHash}
                setOpenLinkHash={setOpenLinkHash}
                setExpanded={setExpanded}
                setFocusProfile={setFocusProfile}
              />
            )}
          </div>
          {expanded && focusProfile && (
            <AssociateCardExpanded
              focusProfile={focusProfile}
              setExpanded={setExpanded}
              setFocusProfile={setFocusProfile}
              hash={hash}
              currentVertical={verticalValue ?? defaultVertical}
              setHash={setHash}
              // @ts-ignore
              associateCardSummary={associateCardSummary}
            />
          )}
        </Box>
      </Row>
    </>
  );
};

export default AssociateCardsContainer;
